import React, { useEffect } from 'react';
import joi from 'joi';
import formErrors from 'constants/formErrors';
import { Button, DynamicForm } from 'components';
import { Props } from 'types/modals';
import useFormHandler from 'hooks/useFormHandler';
import Base from '../../base';
import schema, { triggers } from './schema';
import useApi from 'hooks/useApi';
import endpoints from 'constants/endpoints';

const ResentNotificationModal = ({ closing, onClose, params }: Props) => {
  const formHandler = useFormHandler(schema);
  const api = useApi();

  const handleSubmit = () => {
    const { data, schema } = formHandler;
    const formData = { ...data };

    if (schema.account.hide) {
      delete formData.account;
    }
    if (schema.venue.hide) {
      delete formData.venue;
    }
    if (schema.event?.hide) {
      delete formData.event;
    }
    onClose(formData);
  };

  useEffect(() => {
    api.fetchData(endpoints.GET_NOTIFICATION.get({ id: String(params.id) }));
  }, []);

  useEffect(() => {
    if (api.loaded) {
      const notification = api.response.data;
      const eventName = api.response.event_name;
      formHandler.setFullSchema(
        {
          ...schema,
          account: {
            ...schema.account,
            hide: !notification.account_id,
            defaultLabel: notification.account_name || undefined,
            joi: notification.account_id ? schema.account.joi : joi.allow('').optional()
          },
          venue: {
            ...schema.venue,
            hide: !notification.venue_id,
            defaultLabel: notification.venue_name || undefined,
            joi: notification.venue_id ? schema.venue.joi : joi.allow('').optional()
          },
          event: {
            ...schema.event,
            hide: !notification.event_id,
            joi: notification.event_id ? schema.event.joi : joi.allow('').optional(),
            defaultLabel: eventName || undefined
          }
        },
        {
          title: notification.title,
          message: notification.message,
          trigger: notification.trigger,
          account: notification.account_id ? String(notification.account_id) : '',
          venue: notification.venue_id ? String(notification.venue_id) : '',
          event: eventName || ''
        }
      );
    }
  }, [api.loaded]);

  useEffect(() => {
    const { data, setFullSchema } = formHandler;

    switch (data.trigger) {
      case triggers.ALL_EMPLOYEES:
        setFullSchema(
          {
            ...schema,
            account: {
              ...schema.account,
              hide: true,
              joi: joi.allow('').optional()
            },
            venue: {
              ...schema.venue,
              hide: true,
              joi: joi.allow('').optional()
            },
            event: {
              ...schema.event,
              hide: true,
              joi: joi.allow('').optional()
            },
            isSMS: {
              ...schema.isSMS,
              hide: false
            }
          },
          {
            ...data,
            account: '',
            venue: '',
            event: ''
          }
        );
        break;
      case triggers.ONLY_EMPLOYEES_FROM_ACCOUNT:
        setFullSchema(
          {
            ...schema,
            account: {
              ...schema.account,
              hide: false
            },
            venue: {
              ...schema.venue,
              hide: true,
              joi: joi.allow('').optional()
            },
            event: {
              ...schema.event,
              hide: true,
              joi: joi.allow('').optional()
            },
            isSMS: {
              ...schema.isSMS,
              hide: false
            }
          },
          {
            ...data,
            venue: '',
            event: ''
          }
        );
        break;
      case triggers.ONLY_EMPLOYEES_FROM_VENUE:
        setFullSchema(
          {
            ...schema,
            account: {
              ...schema.account,
              hide: true,
              joi: joi.allow('').optional()
            },
            venue: {
              ...schema.venue,
              hide: false
            },
            event: {
              ...schema.event,
              hide: true,
              joi: joi.allow('').optional()
            },
            isSMS: {
              ...schema.isSMS,
              hide: false
            }
          },
          {
            ...data,
            account: '',
            event: ''
          }
        );
        break;
      case triggers.ONLY_EMPLOYEES_FROM_EVENT:
        setFullSchema(
          {
            ...schema,
            account: {
              ...schema.account,
              hide: true,
              joi: joi.allow('').optional()
            },
            venue: {
              ...schema.venue,
              hide: true,
              joi: joi.allow('').optional()
            },
            event: {
              ...schema.event,
              hide: false,
              joi: joi.string().required().messages({ 'string.empty': formErrors.REQUIRED })
            },
            isSMS: {
              ...schema.isSMS,
              hide: false
            }
          },
          {
            ...data,
            account: '',
            venue: ''
          }
        );
        break;
      default:
        break;
    }
  }, [formHandler.data.trigger]);

  return (
    <Base
      header="Create Notification"
      closing={closing}
      onClose={onClose}
      size="md"
      footer={
        <Button
          text="Create"
          disabled={
            !formHandler.valid ||
            formHandler.isSubmitting ||
            (!formHandler.data.isSMS && !formHandler.data.isEmail && !formHandler.data.isPush)
          }
          onClick={handleSubmit}
        />
      }
    >
      <DynamicForm
        handler={{
          ...formHandler,
          schema: Object.fromEntries(
            Object.entries(formHandler.schema).filter(
              ([key]) => key !== 'isSMS' && key !== 'isEmail' && key !== 'isPush'
            )
          )
        }}
      />
      <div style={{ display: 'flex', gap: '2rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            id="isSMS"
            checked={formHandler.data.isSMS}
            onChange={e => formHandler.setFormData('isSMS')(e.target.checked)}
            disabled={formHandler.schema.isSMS?.disabled}
          />
          <label htmlFor="isSMS" style={{ marginLeft: 8, fontWeight: 500 }}>
            {formHandler.schema.isSMS?.label}
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            id="isEmail"
            checked={formHandler.data.isEmail}
            onChange={e => formHandler.setFormData('isEmail')(e.target.checked)}
            disabled={formHandler.schema.isEmail?.disabled}
          />
          <label htmlFor="isEmail" style={{ marginLeft: 8, fontWeight: 500 }}>
            {formHandler.schema.isEmail?.label}
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            id="isPush"
            checked={formHandler.data.isPush}
            onChange={e => formHandler.setFormData('isPush')(e.target.checked)}
            disabled={formHandler.schema.isPush?.disabled}
          />
          <label htmlFor="isPush" style={{ marginLeft: 8, fontWeight: 500 }}>
            {formHandler.schema.isPush?.label}
          </label>
        </div>
      </div>
    </Base>
  );
};

export default ResentNotificationModal;
